import { createContext, useContext, useReducer } from 'react';
import { NodeChildren, Breadcrumb } from '../../interfaces';

const initialState = {
  breadcrumbNameOverrides: {
    '/conferences': 'Launch list',
    '/groups': 'Groups',
    '/active-packages': 'Active packages',
    '/venuevsbuild': 'Venue build to version set',
    '/error': '',
  } as { [key: string]: string },
};

// Friendly display labels for detail SUB-PAGE segments, keyed by the BARE
// trailing path segment (e.g. '/conferences/view-<id>/packages' -> 'packages').
// These differ from the top-level `breadcrumbNameOverrides` above: a detail
// sub-page always sits under a dynamic `view-<id>` segment, so its full
// accumulated path can't be known ahead of time and a full-path override entry
// can never match it. getBreadcrumbItems therefore falls back to this bare-
// segment map so these crumbs render a friendly name instead of the raw slug.
// Labels are sentence case and space-separated (matching breadcrumbNameOverrides)
// so multi-word slugs (e.g. 'buddylaunches') read naturally in the trail.
const SEGMENT_NAME_OVERRIDES: { [segment: string]: string } = {
  packages: 'Packages',
  permissions: 'Permissions',
  buddylaunches: 'Buddy launches',
  assignee: 'Assignee',
};

interface NavState {
  // Friendly display-name overrides keyed by URL path (e.g. '/conferences' ->
  // 'Launch list', or a dynamically-added '/groups/view-<id>' -> group name).
  // The breadcrumb TRAIL itself is derived statelessly from the current
  // location (see getBreadcrumbItems) — this map only upgrades the label of a
  // path segment when a friendlier name is known.
  breadcrumbNameOverrides: { [key: string]: string };
}

type NavAction = {
  type: 'ADD_BREADCRUMB_OVERRIDE';
  overrides: { [key: string]: string };
};

const NavReducer = (state: NavState, action: NavAction): NavState => {
  switch (action.type) {
    case 'ADD_BREADCRUMB_OVERRIDE':
      return {
        ...state,
        breadcrumbNameOverrides: {
          ...state.breadcrumbNameOverrides,
          ...action.overrides,
        },
      };
    default:
      return state;
  }
};

type Dispatch = (action: NavAction) => void;

/**
 * Register a friendly display-name override for a detail page's breadcrumb.
 *
 * This is an OPTIONAL label enhancement shared by the task and group detail
 * pages: when the entity's name is known (after it loads), the crumb for its
 * detail path shows that name instead of the raw `view-<id>` path segment. If
 * the name is missing (not loaded yet, or the fetch failed), no override is
 * registered and the breadcrumb falls back to the raw segment — still a valid,
 * clickable crumb. The crumb's href is always derived from `detailPath`
 * (see getBreadcrumbItems), so it stays navigable by id regardless of the label.
 *
 * @param dispatch   the nav dispatcher (from useNavDispatch)
 * @param detailPath the detail page path, e.g. `/conferences/view-<id>` or `/groups/view-<id>`
 * @param name       the entity's display name, if known
 */
export function overrideDetailBreadcrumb(
  dispatch: Dispatch,
  detailPath: string,
  name?: string | null
): void {
  if (name) {
    dispatch({
      type: 'ADD_BREADCRUMB_OVERRIDE',
      overrides: { [detailPath]: name },
    });
  }
}

const NavStateContext = createContext<NavState | undefined>(undefined);
const NavDispatchContext = createContext<Dispatch | undefined>(undefined);

/**
 * Returns the current nav state
 */
export function useNavState() {
  const context = useContext(NavStateContext);
  if (context === undefined) {
    throw new Error('useNavState must be used within a NavProvider');
  }

  return context;
}

/**
 * Returns a dispatcher to perform updates to the nav state
 */
export function useNavDispatch() {
  const context = useContext(NavDispatchContext);
  if (context === undefined) {
    throw new Error('useNavDispatch must be used within a NavProvider');
  }
  return context;
}

/**
 * Component for provider nav state and dispatcher
 * to child components
 */
export const NavProvider = ({ children }: NodeChildren) => {
  const [data, dispatch] = useReducer(NavReducer, initialState);

  return (
    <NavStateContext.Provider value={data}>
      <NavDispatchContext.Provider value={dispatch}>
        {children}
      </NavDispatchContext.Provider>
    </NavStateContext.Provider>
  );
};

/**
 * Build the breadcrumb trail for a given URL path. This is a PURE function of
 * the current path (plus a static/known override map for friendlier labels), so
 * the trail is fully reconstructable on page load and reload without any browser
 * navigation history or accumulated state. This is what keeps breadcrumbs intact
 * across a browser refresh on a deep page (e.g. a task/group detail page).
 *
 * Example: '/conferences/view-<taskA>/packages' ->
 *   Fusion Launch Coordinator > Launch list > view-<taskA> > Packages
 *
 * `breadCrumbOverrides` only changes the DISPLAYED label of a path segment when a
 * friendlier name is known (e.g. '/groups/view-<id>' -> the group's name). A
 * missing override falls back to a known bare-segment label (SEGMENT_NAME_OVERRIDES,
 * for detail sub-pages like 'packages'), and failing that to the raw path segment,
 * which is still a valid, navigable crumb — so the trail never depends on that data
 * being loaded.
 */
export function getBreadcrumbItems(
  pathName: string,
  breadCrumbOverrides: { [key: string]: string }
): Breadcrumb[] {
  let urlPaths: string[] = ['/'];

  if (pathName !== '/') {
    // Path other than homepage
    urlPaths = pathName.split('/');
  }

  // First item in the breadcrumb is always the homepage
  const breadcrumbItems: Breadcrumb[] = [
    { text: 'Fusion Launch Coordinator', href: '#/' },
  ];

  let pathBuilder = '';

  // By default, breadcrumbs are generated from the paths in the URL. A page can
  // override the displayed label for a path by adding an entry in the
  // breadCrumbOverrides map (static entries live in the initial nav state;
  // dynamic ones are added via ADD_BREADCRUMB_OVERRIDE once a name is known).
  for (let i = 1; i < urlPaths.length; i++) {
    // Use the override label for the path if one exists, else the raw segment.
    const pageName = decodeURIComponent(urlPaths[i]);
    pathBuilder += '/' + urlPaths[i];

    // Precedence: an exact full-path override (top-level routes and dynamic
    // entity names) wins; then a static bare-segment override for known detail
    // sub-pages (which sit under a dynamic view-<id> and so can't be matched by
    // a full-path entry); otherwise the raw (decoded) path segment.
    let text: string;
    if (breadCrumbOverrides[pathBuilder] !== undefined) {
      text = breadCrumbOverrides[pathBuilder]!;
    } else if (SEGMENT_NAME_OVERRIDES[pageName] !== undefined) {
      text = SEGMENT_NAME_OVERRIDES[pageName]!;
    } else {
      text = pageName;
    }

    breadcrumbItems.push({
      text,
      href: '#' + pathBuilder,
    });
  }
  // Do not display a breadcrumb on the home page (single item).
  return breadcrumbItems.length > 1 ? breadcrumbItems : [];
}
